import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export type PageTopBarConfig = {
  /** When set, replaces the auto-generated breadcrumb for this page. */
  breadcrumb?: string[];
  /** Right-side TopBar actions (Live, bell, page buttons). */
  actions?: React.ReactNode;
};

type PageTopBarContextValue = {
  breadcrumb?: string[];
  actionsRef: React.RefObject<React.ReactNode>;
  /** Bumped when the active page registers actions so AppLayout re-reads the ref. */
  actionsTick: number;
  setBreadcrumb: (breadcrumb: string[] | undefined) => void;
  syncActions: () => void;
};

const PageTopBarContext = createContext<PageTopBarContextValue | null>(null);

function breadcrumbKey(breadcrumb: string[] | undefined): string {
  return breadcrumb?.length ? JSON.stringify(breadcrumb) : '';
}

export function PageTopBarProvider({ children }: { children: React.ReactNode }) {
  const [breadcrumb, setBreadcrumbState] = useState<string[] | undefined>();
  const [actionsTick, setActionsTick] = useState(0);
  const actionsRef = useRef<React.ReactNode>(null);

  const setBreadcrumb = useCallback((next: string[] | undefined) => {
    setBreadcrumbState((prev) => {
      const prevKey = breadcrumbKey(prev);
      const nextKey = breadcrumbKey(next);
      return prevKey === nextKey ? prev : next;
    });
  }, []);

  const syncActions = useCallback(() => {
    setActionsTick((t) => t + 1);
  }, []);

  const value = useMemo((): PageTopBarContextValue => {
    const next: PageTopBarContextValue = { actionsRef, actionsTick, setBreadcrumb, syncActions };
    if (breadcrumb !== undefined) next.breadcrumb = breadcrumb;
    return next;
  }, [breadcrumb, actionsTick, setBreadcrumb, syncActions]);

  return <PageTopBarContext.Provider value={value}>{children}</PageTopBarContext.Provider>;
}

export function usePageTopBarContext() {
  const ctx = useContext(PageTopBarContext);
  if (!ctx) {
    throw new Error('usePageTopBarContext must be used within PageTopBarProvider');
  }
  return ctx;
}

/** Register page TopBar breadcrumb override and right-side actions. */
export function usePageTopBar(config: PageTopBarConfig, actionDeps: React.DependencyList = []) {
  const { actionsRef, setBreadcrumb, syncActions } = usePageTopBarContext();
  actionsRef.current = config.actions ?? null;

  const key = breadcrumbKey(config.breadcrumb);

  useLayoutEffect(() => {
    setBreadcrumb(key ? (JSON.parse(key) as string[]) : undefined);
    syncActions();
    return () => setBreadcrumb(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- actionDeps drives TopBar action refresh
  }, [setBreadcrumb, syncActions, key, ...actionDeps]);
}

/** Clear TopBar actions for pages that manage actions in-content (e.g. settings). */
export function useClearPageTopBarActions() {
  const { actionsRef, syncActions } = usePageTopBarContext();
  actionsRef.current = null;

  useLayoutEffect(() => {
    syncActions();
  }, [syncActions]);
}
